import styled from '@emotion/styled';
import {
  Button,
  Drawer,
  Input,
  message,
  Modal,
  Progress,
  Select,
  Upload,
} from 'antd';

import { useEffect, useState } from 'react';

import { MdClose } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { ConnectedUserEntity } from '../../../entities/ConnectedUserEntity';
import { Layout } from '../../../modules/shared/Layout';
import { ORANGE, PRIMARY, YELLOW } from '../../../shared/colors';
import { formatNum } from '../../../shared/constants';
import {
  addAML,
  addKYC,
  getUserAML,
  getUserKYC,
  getUserPortefeuille,
  getUserPortefeuilleKPI,
  updateAML,
  updateKYC,
} from '../../dashboard/components/Monitor/network';
import { BASE_MEDIA } from '../../shared/ApiRoutes';
import Loading from '../../shared/Loading';
import { StatusPill, StatusTone } from '../shared/StatusPill';

const Container = styled.div`
  min-height: calc(100vh - 76px);
  background:
    radial-gradient(circle at 8% 0%, rgba(255, 79, 31, 0.08), transparent 24%),
    radial-gradient(circle at 92% 8%, rgba(255, 176, 0, 0.08), transparent 22%),
    var(--mt-bg);
  color: var(--mt-text);
  font-family: Poppins-R;

  > .content {
    width: 100%;
    padding: 12px;
  }

  .profile-hero {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 18px;
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.13), transparent 38%),
      ${PRIMARY};
    box-shadow: var(--mt-card-shadow);
  }

  .eyebrow {
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .profile-hero h1 {
    margin: 6px 0 0;
    color: #fff7e8;
    font-family: Poppins-B;
    font-size: 30px;
    line-height: 1.1;
  }

  .profile-hero p {
    max-width: 760px;
    margin: 8px 0 0;
    color: rgba(255, 247, 232, 0.72);
    line-height: 1.55;
  }

  .hero-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .primary-action {
    border: 0;
    border-radius: 999px;
    background: linear-gradient(135deg, ${ORANGE}, ${YELLOW});
    color: #07141c;
    font-family: Poppins-B;
  }

  .ghost-action {
    border-color: rgba(255, 176, 0, 0.28);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.06);
    color: #fff7e8;
    font-family: Poppins-B;
  }

  .profile-workspace {
    display: grid;
    grid-template-columns: 390px minmax(0, 1fr);
    gap: 12px;
    margin-top: 12px;
  }

  .side-stack,
  .kyc-form {
    display: grid;
    gap: 12px;
    align-content: start;
  }

  .panel {
    overflow: hidden;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    background: var(--mt-card);
    box-shadow: var(--mt-card-shadow);
  }

  .panel-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 46px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--mt-border);
    background: ${PRIMARY};
    color: ${YELLOW};
    font-family: Poppins-B;
    text-transform: uppercase;
  }

  .panel-body {
    padding: 14px;
  }

  .profile-card {
    display: grid;
    grid-template-columns: 58px minmax(0, 1fr);
    gap: 12px;
    align-items: center;
    padding: 12px;
    border: 1px solid var(--mt-border);
    border-radius: 10px;
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.08), transparent 42%),
      var(--mt-surface);
  }

  .avatar {
    display: grid;
    place-items: center;
    width: 58px;
    height: 58px;
    border-radius: 10px;
    color: #07141c;
    background: linear-gradient(135deg, ${ORANGE}, ${YELLOW});
    font-family: Poppins-B;
    font-size: 20px;
  }

  .profile-card h2 {
    margin: 0;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 18px;
    line-height: 1.2;
  }

  .muted {
    color: var(--mt-muted);
    font-size: 12px;
  }

  .metric-grid,
  .detail-grid,
  .field-grid,
  .documents-grid {
    display: grid;
    gap: 10px;
  }

  .metric-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    margin-top: 12px;
  }

  .detail-grid,
  .field-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .documents-grid {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }

  .metric,
  .detail-item,
  .upload-card {
    border: 1px solid var(--mt-border);
    border-radius: 9px;
    padding: 10px;
    background: var(--mt-surface);
  }

  .metric span,
  .detail-item span,
  .field label,
  .upload-card label {
    display: block;
    color: var(--mt-muted);
    font-family: Poppins-B;
    font-size: 10px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .metric strong,
  .detail-item strong {
    display: block;
    margin-top: 5px;
    color: var(--mt-text);
    font-family: Poppins-B;
    overflow-wrap: anywhere;
  }

  .completion-copy {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 9px;
    font-family: Poppins-B;
    font-size: 13px;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .field.full {
    grid-column: 1 / -1;
  }

  .ant-input,
  .ant-select-selector,
  textarea.ant-input {
    min-height: 38px;
    border-color: var(--mt-border) !important;
    border-radius: 9px !important;
    background: var(--mt-surface) !important;
    color: var(--mt-text) !important;
    box-shadow: none !important;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    color: var(--mt-text) !important;
  }

  .section-heading {
    margin: 8px 0 10px;
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 13px;
    text-transform: uppercase;
  }

  .upload-card {
    display: flex;
    min-height: 152px;
    flex-direction: column;
    justify-content: space-between;
    gap: 8px;
  }

  .upload-preview {
    display: grid;
    min-height: 58px;
    place-items: center;
    overflow: hidden;
    border: 1px dashed var(--mt-border);
    border-radius: 8px;
    color: var(--mt-muted);
    font-size: 12px;
  }

  .photo {
    width: 100%;
    height: 76px;
    object-fit: cover;
    cursor: zoom-in;
  }

  .upload-button {
    width: 100%;
    border-color: rgba(255, 176, 0, 0.36);
    border-radius: 999px;
    background: var(--mt-card);
    color: var(--mt-warning);
    font-family: Poppins-B;
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
  }

  @media (max-width: 1180px) {
    .profile-workspace,
    .metric-grid,
    .detail-grid,
    .field-grid,
    .documents-grid {
      grid-template-columns: 1fr;
    }
  }
`;

export const ProfilKycPage = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    birth_date: '',
    nationality: '',
    id_type: '',
    id_number: '',
    address: '',
    employment_status: '',
    income_level: '',
    investment_purpose: '',
    politically_exposed: 'non',
    country_of_residence: '',
  });

  const [files, setFiles] = useState<{ [key: string]: File }>({});
  const [loading, setLoading] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [kyc, setKYC] = useState<any>({});
  const [aml, setAML] = useState<any>({});
  const [portefeuilleKPI, setPortefeuilleKPI] = useState<any>();
  const [completion, setCompletion] = useState(0);
  const [previewImage, setpreviewImage] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;

  // const steps = [
  //   { label: 'Informations personnelles', status: 'done' },
  //   { label: 'Pièce d’identité reçue', status: 'done', date: '09/07/2025' },
  //   {
  //     label: 'Justificatif de domicile',
  //     status: 'pending',
  //   },
  //   { label: 'Vérification manuelle', status: 'todo' },
  //   { label: 'Compte activé', status: 'todo', isLock: true },
  // ];

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInput = (e) => {
    const { name, value } = e.target;
    const updated = { ...formData, [name]: value };
    setFormData(updated);
    updateProgress(updated, files);
  };

  const handleFile = (name, file) => {
    const updated = { ...files, [name]: file };
    setFiles(updated);
    updateProgress(formData, updated);
  };

  const fetchData = async () => {
    setLoading(true);

    await getUserKYC(connectedUser.id).then(async ({ data }) => {
      setKYC(data);
      if (data.error) {
        if (data.error === 'KYC introuvable') {
        }
      } else {
        setKYC(data);
        // Préremplir les valeurs texte
        setFormData({
          full_name: data.full_name || '',
          birth_date: data.birth_date ? data.birth_date.slice(0, 10) : '',
          nationality: data.nationality || '',
          id_type: data.id_type || '',
          id_number: data.id_number || '',
          address: data.address || '',
          employment_status: data.employment_status || '',
          income_level: data.income_level || '',
          investment_purpose: data.investment_purpose || '',
          politically_exposed:
            typeof data.politically_exposed === 'boolean'
              ? data.politically_exposed
              : false,
          country_of_residence: data.country_of_residence || '',
        });

        await preloadFilesFromKYC(data, setFiles);
        updateProgress(data, {});
      }
    });

    await getUserAML(connectedUser.id).then(async ({ data }) => {
      setAML(data);
      if (data.error) {
        if (data.error === 'AML introuvable') {
        }
      } else {
        setAML(data);
      }
    });

    await getUserPortefeuille(connectedUser.id);

    await getUserPortefeuilleKPI(connectedUser.id).then(({ data }) => {
      setPortefeuilleKPI(data);
    });
    setLoading(false);
  };

  const preloadFilesFromKYC = async (kycData, setFiles) => {
    const fileKeys = [
      'id_document_front',
      'id_document_back',
      'selfie',
      'proof_of_residency',
    ];

    for (const key of fileKeys) {
      const fileUrl = kycData[key];
      if (fileUrl) {
        try {
          const response = await fetch(BASE_MEDIA + fileUrl);
          const blob = await response.blob();

          const fileExtension = fileUrl.split('.').pop();
          const filename = `${key}.${fileExtension}`;

          const file = new File([blob], filename, { type: blob.type });

          setFiles((prev) => ({
            ...prev,
            [key]: file,
          }));
        } catch (error) {
          console.error(`Erreur lors du chargement du fichier ${key}`, error);
        }
      }
    }
  };

  const updateProgress = (data, fileData) => {
    const totalFields = 14;
    let filled = 0;
    Object.values(data).forEach((val) => {
      if (val && val !== '') filled++;
    });
    Object.values(fileData).forEach((val) => {
      if (val) filled++;
    });
    const percent = Math.min(Math.round((filled / totalFields) * 100), 100);
    setCompletion(percent);
  };

  const handleSubmit = async () => {
    setLoading(true);
    const data = new FormData();

    // Ajoute les champs texte
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        data.append(key, value);
      }
    });

    // Ajoute seulement les fichiers nouvellement sélectionnés
    Object.entries(files).forEach(([key, value]) => {
      if (value) {
        data.append(key, value);
      }
    });

    data.append('user', connectedUser.id);

    try {
      if (kyc && kyc.id) {
        await updateKYC(kyc.id, data);
        message.success('Informations KYC mises à jour');
      } else {
        await addKYC(data);
        // ajouter le AML
        const data1 = new FormData();
        data1.append('income_level', formData.income_level);
        data1.append('country_of_residence', formData.country_of_residence);
        data1.append('is_pep', formData.politically_exposed);
        data1.append('user', connectedUser.id);
        await addAML(data1);
        message.success('KYC soumis avec succès');
      }

      // ajouter le AML
      if (aml && aml.id) {
        const data1 = new FormData();
        data1.append('income_level', formData.income_level);
        data1.append('country_of_residence', formData.country_of_residence);
        data1.append('is_pep', formData.politically_exposed);
        data1.append('user', connectedUser.id);
        await updateAML(aml.id, data1);
      } else {
        const data1 = new FormData();
        data1.append('income_level', formData.income_level);
        data1.append('country_of_residence', formData.country_of_residence);
        data1.append('is_pep', formData.politically_exposed);
        data1.append('user', connectedUser.id);
        await addAML(data1);
      }

      await fetchData(); // Recharge les données
    } catch (error) {
      message.error('Erreur lors de la soumission');
    }

    setLoading(false);
  };

  const showModal = (val) => {
    setIsModalVisible(true);
    setpreviewImage(val);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const renderImageUpload = (name: string, label: string, alt: string) => (
    <div className='upload-card'>
      <label>{label}</label>
      <div className='upload-preview'>
        {kyc[name] ? (
          <img
            src={BASE_MEDIA + kyc[name]}
            className='photo'
            alt={alt}
            onClick={() => showModal(BASE_MEDIA + kyc[name])}
          />
        ) : (
          <span>Document manquant</span>
        )}
      </div>
      <Upload
        accept='image/*'
        beforeUpload={(file) => {
          const isImage = file.type.startsWith('image/');
          if (!isImage) {
            message.error('Seules les images sont autorisées');
            return Upload.LIST_IGNORE;
          }

          handleFile(name, file);
          return false;
        }}
        showUploadList={false}
      >
        <Button className='upload-button'>Téléverser</Button>
      </Upload>
    </div>
  );

  const renderFileUpload = (name: string, label: string) => (
    <div className='upload-card'>
      <label>{label}</label>
      <div className='upload-preview'>
        {kyc[name] ? (
          <a href={BASE_MEDIA + kyc[name]} target='_blank' rel='noopener noreferrer'>
            <StatusPill tone='success'>Disponible</StatusPill>
          </a>
        ) : (
          <span>Document manquant</span>
        )}
      </div>
      <Upload
        beforeUpload={(file) => {
          handleFile(name, file);
          return false;
        }}
        showUploadList={false}
      >
        <Button className='upload-button'>Téléverser</Button>
      </Upload>
    </div>
  );

  return loading ? (
    <Loading />
  ) : (
    <Layout>
      <Container>
        <div className='content'>
          <section className='profile-hero'>
            <div>
              <span className='eyebrow'>Espace client</span>
              <h1>Profil, portefeuille et conformité</h1>
              <p>
                Consultez vos informations, votre portefeuille estimé et
                complétez votre dossier KYC/AML dans un espace sécurisé.
              </p>
            </div>
            <div className='hero-actions'>
              <Button
                className='ghost-action'
                onClick={() => setShowDrawer(true)}
                loading={loading}
              >
                Dépôt Orange Money
              </Button>
              <Button
                className='primary-action'
                onClick={handleSubmit}
                loading={loading}
              >
                {kyc && kyc.id ? 'Mettre à jour KYC' : 'Soumettre KYC'}
              </Button>
            </div>
          </section>

          <section className='profile-workspace'>
            <div className='side-stack'>
              <div className='panel'>
                <div className='panel-title'>Profil utilisateur</div>
                <div className='panel-body'>
                  <div className='profile-card'>
                    <div className='avatar'>{getUserInitials(connectedUser)}</div>
                    <div>
                      <h2>{getUserName(connectedUser)}</h2>
                      <span className='muted'>{connectedUser?.email || '--'}</span>
                    </div>
                  </div>
                  <div className='detail-grid' style={{ marginTop: 12 }}>
                    <div className='detail-item'>
                      <span>Agence</span>
                      <strong>{connectedUser?.agence?.nom || '--'}</strong>
                    </div>
                    <div className='detail-item'>
                      <span>Partenaire</span>
                      <strong>{connectedUser?.partenaire?.nom || '--'}</strong>
                    </div>
                    <div className='detail-item'>
                      <span>Profil</span>
                      <strong>
                        {connectedUser?.profils?.map((profil) => profil.nom).join(', ') || '--'}
                      </strong>
                    </div>
                    <div className='detail-item'>
                      <span>Statut KYC</span>
                      <strong>
                        <StatusPill tone={getKycStatusTone(kyc?.status)}>
                          {getKycStatusLabel(kyc?.status)}
                        </StatusPill>
                      </strong>
                    </div>
                  </div>
                </div>
              </div>

              <div className='panel'>
                <div className='panel-title'>Portefeuille estimé</div>
                <div className='panel-body metric-grid'>
                  <div className='metric'>
                    <span>Total estimé</span>
                    <strong>
                      {portefeuilleKPI
                        ? `${formatNum(portefeuilleKPI?.valeurPortefeuille)} XAF`
                        : '--'}
                    </strong>
                  </div>
                  <div className='metric'>
                    <span>Rendement</span>
                    <strong>{getPortfolioYield(portefeuilleKPI)} %</strong>
                  </div>
                  <div className='metric'>
                    <span>PNL</span>
                    <strong>
                      {portefeuilleKPI ? `${formatNum(portefeuilleKPI?.pnl)} XAF` : '--'}
                    </strong>
                  </div>
                </div>
              </div>

              <div className='panel'>
                <div className='panel-title'>Progression KYC</div>
                <div className='panel-body'>
                  <div className='completion-copy'>
                    <strong>Dossier complété</strong>
                    <span>{completion}%</span>
                  </div>
                  <Progress
                    percent={completion}
                    size='small'
                    strokeColor={completion === 100 ? '#008f4f' : ORANGE}
                    status={completion === 100 ? 'success' : 'active'}
                  />
                  <div className='detail-grid' style={{ marginTop: 12 }}>
                    <div className='detail-item'>
                      <span>Score AML</span>
                      <strong>{aml?.aml_risk_score || '--'}</strong>
                    </div>
                    <div className='detail-item'>
                      <span>Niveau AML</span>
                      <strong>
                        <StatusPill tone={getAmlRiskTone(aml?.aml_risk_level)}>
                          {aml?.aml_risk_level || 'Non calculé'}
                        </StatusPill>
                      </strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='panel'>
              <div className='panel-title'>Soumettre vos informations KYC & AML</div>
              <div className='panel-body kyc-form'>
                <div>
                  <div className='section-heading'>Identité & résidence</div>
                  <div className='field-grid'>
                    <div className='field'>
                      <label>Date de naissance</label>
                      <Input
                        type='date'
                        name='birth_date'
                        value={formData.birth_date}
                        onChange={handleInput}
                      />
                    </div>
                    <div className='field'>
                      <label>Nationalité</label>
                      <Input
                        name='nationality'
                        placeholder='Nationalité'
                        value={formData.nationality}
                        onChange={handleInput}
                      />
                    </div>
                    <div className='field'>
                      <label>Pays de résidence</label>
                      <Input
                        name='country_of_residence'
                        placeholder='Pays de résidence'
                        value={formData.country_of_residence}
                        onChange={handleInput}
                      />
                    </div>
                    <div className='field'>
                      <label>Type de pièce</label>
                      <Input
                        name='id_type'
                        placeholder='CNI, Passeport...'
                        value={formData.id_type}
                        onChange={handleInput}
                      />
                    </div>
                    <div className='field'>
                      <label>Numéro de pièce</label>
                      <Input
                        name='id_number'
                        placeholder='Numéro de pièce'
                        value={formData.id_number}
                        onChange={handleInput}
                      />
                    </div>
                    <div className='field'>
                      <label>Adresse complète</label>
                      <Input
                        name='address'
                        placeholder='Adresse complète'
                        value={formData.address}
                        onChange={handleInput}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <div className='section-heading'>Documents justificatifs</div>
                  <div className='documents-grid'>
                    {renderImageUpload('id_document_front', 'Pièce recto', 'Document recto')}
                    {renderImageUpload('id_document_back', 'Pièce verso', 'Document verso')}
                    {renderImageUpload('selfie', 'Selfie', 'Selfie')}
                    {renderFileUpload('proof_of_residency', 'Justif. domicile')}
                    {renderFileUpload('proof_of_revenue', 'Justif. revenu')}
                  </div>
                </div>

                <div>
                  <div className='section-heading'>Profil financier & AML</div>
                  <div className='field-grid'>
                    <div className='field'>
                      <label>Profession</label>
                      <Input
                        name='employment_status'
                        placeholder='Profession'
                        value={formData.employment_status}
                        onChange={handleInput}
                      />
                    </div>
                    <div className='field'>
                      <label>Objectif d'investissement</label>
                      <Input
                        name='investment_purpose'
                        placeholder="Objectif d'investissement"
                        value={formData.investment_purpose}
                        onChange={handleInput}
                      />
                    </div>
                    <div className='field'>
                      <label>Personne politiquement exposée</label>
                      <Select
                        value={formData.politically_exposed}
                        onChange={(value) =>
                          setFormData((prev) => ({
                            ...prev,
                            politically_exposed: value,
                          }))
                        }
                        options={[
                          { label: 'Oui', value: true },
                          { label: 'Non', value: false },
                        ]}
                      />
                    </div>
                    <div className='field'>
                      <label>Revenu</label>
                      <Select
                        value={formData.income_level}
                        onChange={(value) =>
                          setFormData((prev) => ({
                            ...prev,
                            income_level: value,
                          }))
                        }
                        options={[
                          { value: 'Non déclaré', label: 'Non déclaré' },
                          { value: 'Faible', label: 'Faible (< 100 000 XAF/mois)' },
                          {
                            value: 'Moyen',
                            label: 'Moyen (100 000 à 500 000 XAF/mois)',
                          },
                          { value: 'Élevé', label: 'Élevé (> 500 000 XAF/mois)' },
                          {
                            value: 'Très élevé',
                            label: 'Très élevé (> 1 000 000 XAF/mois)',
                          },
                        ]}
                      />
                    </div>
                  </div>
                </div>

                <div className='form-actions'>
                  <Button
                    onClick={handleSubmit}
                    loading={loading}
                    className='primary-action'
                  >
                    {kyc && kyc.id ? 'Mettre à jour' : 'Soumettre'}
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </div>
        <Drawer
          title='Recharger votre compte via Orange Money'
          placement='right'
          width={400}
          onClose={() => setShowDrawer(false)}
          open={showDrawer}
          closeIcon={<MdClose size={20} />}
        >
          <div style={{ margin: 5 }}>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <Input
                style={{ margin: 5 }}
                name='number_tel'
                placeholder='Numero de telephone'
                value=''
                onChange={handleInput}
              />
              <Input
                style={{ margin: 5 }}
                name='montant'
                placeholder='Montant'
                value=''
                onChange={handleInput}
              />
            </div>

            <div className='pt-6'>
              <Button
                loading={loading}
                style={{ margin: 5, background: 'orange' }}
                className='w-full md:w-auto'
                onClick={() => setLoading(true)}
              >
                Payer
              </Button>
            </div>
          </div>
        </Drawer>
      </Container>
      <Modal
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        centered
      >
        <img
          src={previewImage}
          alt='Aperçu agrandi'
          style={{ width: '100%' }}
        />
      </Modal>
    </Layout>
  );
};

const getUserName = (user?: ConnectedUserEntity) =>
  `${user?.prenom || ''} ${user?.nom || ''}`.trim() || 'Utilisateur';

const getUserInitials = (user?: ConnectedUserEntity) => {
  const prenom = user?.prenom?.[0] || '';
  const nom = user?.nom?.[0] || '';
  return `${prenom}${nom}`.toUpperCase() || 'CL';
};

const getPortfolioYield = (portefeuilleKPI: any) => {
  if (!portefeuilleKPI) return '--';

  return (
    Math.round(
      (portefeuilleKPI?.rendement * (1 + 0.001 / 100) + Number.EPSILON) * 100,
    ) / 100
  );
};

const getKycStatusLabel = (status?: string) => {
  if (status === 'verified') return 'Vérifié';
  if (status === 'submitted') return 'Soumis';
  if (status === 'pending') return 'En attente';
  if (status === 'rejected') return 'Rejeté';
  return 'Non soumis';
};

const getKycStatusTone = (status?: string): StatusTone => {
  if (status === 'verified') return 'success';
  if (status === 'rejected') return 'danger';
  if (status === 'submitted' || status === 'pending') return 'warning';
  return 'neutral';
};

const getAmlRiskTone = (risk?: string): StatusTone => {
  const normalizedRisk = risk?.toLowerCase();
  if (normalizedRisk === 'faible') return 'success';
  if (normalizedRisk === 'moyen') return 'warning';
  if (normalizedRisk === 'eleve' || normalizedRisk === 'élevé') return 'danger';
  return 'neutral';
};
